import { BYOButton, Headline1, Paragraph } from '@cdm/quickenloans-library/lib';
import PropTypes from 'prop-types';
import React, { isValidElement } from 'react';
import FeaturedImage from '../../Atoms/FeaturedImage/FeaturedImage';
import LearningCenterSourceIdLink from '../LearningCenterSourceIdLink/LearningCenterSourceIdLink';
import { defaultAttributes, testAttributes } from './Constants';
import 
{
    ctasSection,
    defaultBackground,
    heroBackground,
    heroContainer,
    heroHeadline,
    imageSection,
    subTitleSection,
    textSection
} from './GenericHeroWithImage.module.scss';

const genButtons = (buttons, articleId) => buttons?.map((item, index) => (
    <BYOButton
        key={`cta-button-${String(index)}`}
        as={LearningCenterSourceIdLink}
        color={defaultAttributes.color}
        style={!item?.buttonClasses?.includes(testAttributes.CTA_VARIANT_OUTLINED) ? defaultAttributes.filled : defaultAttributes.outlined}
        href={item.href}
        articleId={articleId}
        testId={`${testAttributes.CTA_BUTTON}${index}`}
        iconRight={item.buttonIcon}
        iconTestId={`${testAttributes.CTA_BUTTON_ICON}${index}`}
    >
        {item.buttonText}
    </BYOButton>
));

const shouldRenderCtas = (ctas) => isValidElement(ctas) || (Array.isArray(ctas) && ctas.length > 0);

const renderCtas = (ctas, articleId) =>
{
    if (isValidElement(ctas)) return ctas;
    if (Array.isArray(ctas) && ctas.length > 0) return genButtons(ctas, articleId);
    return null;
}

const GenericHeroWithImage = ({ 
    title = "", 
    description = "",
    subTitle = "",
    image = "",
    imgAlt = "representation of the image",
    headline: { Component: Headline } = { Component: false },
    backgroundColor = {},
    ctas = {},
    articleId = "",
}) => (
    <div 
        className={`${heroBackground} ${!Object.keys(backgroundColor).length ? defaultBackground : '' } ql-block-group-fullwidth`} 
        data-testid="generic-hero-with-image"
        {...(backgroundColor && { style: { ...backgroundColor } })}
    >
        <div className={`${heroContainer} qld-grid`}>
            <div className={textSection}>
                {Headline 
                    ? <Headline className={heroHeadline} data-testid="hero-title">{title}</Headline>
                    : <Headline1 className="h1_semi_bold_newSize" data-testid="hero-title">{title}</Headline1>
                }
                {subTitle && <Paragraph className={`para_regular_newSize ${subTitleSection}`} data-testid="hero-sub-title">{subTitle}</Paragraph>}
                <Paragraph className="para_regular_newSize" data-testid="hero-text">{description}</Paragraph>
            </div>

            {shouldRenderCtas(ctas) &&
                <div className={ctasSection}>
                    {renderCtas(ctas, articleId)}
                </div>
            }

            {
                ['object', 'string'].includes(typeof image)  && <div className={imageSection}>
                    <FeaturedImage
                        gatsbyFeaturedImage={typeof image === 'object' ? image?.image : null}
                        imgPath={typeof image === 'string' ? image : ''}
                        imgAlt={imgAlt}
                        testId={`${testAttributes.HERO_IMAGE}`}
                    />
                </div>
            }
        </div>
    </div>
);

GenericHeroWithImage.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    subTitle: PropTypes.string,
    image: PropTypes.oneOfType([
        PropTypes.string, 
        PropTypes.bool,
        PropTypes.object,
    ]),
    headline: PropTypes.shape({
        Component: PropTypes.elementType,
    }),
    backgroundColor: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string,
    ]),
    ctas: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.shape({
            href: PropTypes.string,
            buttonText: PropTypes.string,
            buttonIcon: PropTypes.node,
            buttonClasses: PropTypes.string,
            buttonAttributes: PropTypes.object,
        })),
    ]),
    articleId: PropTypes.string,
    imgAlt: PropTypes.string,
};

export default GenericHeroWithImage;
